import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ru, uk, enUS } from 'date-fns/locale';
import { Locale } from 'react-day-picker/locale';

import { Paragraph, ShareButton } from '@app/shared/ui/atoms/Common';
import { SlideContainer, SlideHeading } from '@app/shared/ui/atoms/DailyEnergy';
import {
  MOON_IMAGE_SPRING_CONFIGS_MAP,
  SYMBOL_IMAGE_SPRING_CONFIGS_MAP,
} from '@app/shared/constants/dailyEnergy';

interface SlideOneContentProps {
  moonImageSrc: string;
  moonPhaseName: string;
  symbolSrc: string;
  symbolName: string;
  dayQuote: string;
  day: number;
}

const localeMap: Record<string, Locale> = {
  ru,
  uk,
  en: enUS,
};

export function SlideOneContent({
  moonImageSrc,
  moonPhaseName,
  symbolSrc,
  symbolName,
  dayQuote,
  day,
}: SlideOneContentProps) {
  const { t, i18n } = useTranslation();
  const today = new Date();

  const lang = i18n.resolvedLanguage ?? 'en';

  const formattedDate = format(today, 'dd.MM.yyyy EEEE', {
    locale: localeMap[lang] ?? enUS,
  });

  const moonImageSprings = useSpring({ ...MOON_IMAGE_SPRING_CONFIGS_MAP[1] });
  const symbolImageSprings = useSpring({ ...SYMBOL_IMAGE_SPRING_CONFIGS_MAP[1] });

  return (
    <SlideContainer>
      <div className="flex items-center justify-center">
        <span className="flex-1 overflow-x-hidden wrap-break-word text-end text-xs font-semibold text-foreground">
          {formattedDate}
        </span>

        <animated.img
          style={moonImageSprings}
          src={moonImageSrc}
          alt="moon"
          className="w-[200px] p-4"
        />

        <span className="flex-1 overflow-x-hidden wrap-break-word text-xs font-semibold text-foreground">
          {moonPhaseName}
        </span>
      </div>

      <div className="flex flex-col items-center">
        <animated.img
          style={symbolImageSprings}
          src={symbolSrc}
          alt="symbol"
          className="w-[120px]"
        />

        <SlideHeading>{`${String(day)} ${t('daily_energy.lunar_day')}`}</SlideHeading>

        <SlideHeading>
          <span className="font-normal">{t('daily_energy.symbol_of_the_day')}:</span> {symbolName}
        </SlideHeading>

        {/*<div className="mt-2.5 flex gap-5">*/}
        {/*  <span className="whitespace-pre-line text-end text-2xs text-foreground">*/}
        {/*    {`${t('daily_energy.start')} ${MOCK_SLIDE_ONE_CONTENT.start.date}\n${MOCK_SLIDE_ONE_CONTENT.start.time}`}*/}
        {/*  </span>*/}

        {/*  <span className="whitespace-pre-line text-2xs text-foreground">*/}
        {/*    {`${t('daily_energy.end')} ${MOCK_SLIDE_ONE_CONTENT.end.date}\n${MOCK_SLIDE_ONE_CONTENT.end.time}`}*/}
        {/*  </span>*/}
        {/*</div>*/}
      </div>

      <Paragraph
        variant="white"
        className="mt-10 rounded-[10px] bg-[#000E1B3D] px-[12%] py-4 text-center font-normal backdrop-blur-xs"
      >
        {dayQuote}
      </Paragraph>

      <ShareButton className="bg-card min-h-[40px] mt-1" />

      <Paragraph className="mt-auto pt-10 text-center text-2xs text-gray-neutral">
        {t('daily_energy.bottom_note')}
      </Paragraph>
    </SlideContainer>
  );
}
